import { CURRENT_USER_GROUP_ORGS } from "@/api/queries/currentUserGroupOrgs";
import { useEffect } from "react";
import {
  useReactiveVar,
  useLazyQuery,
  useQuery,
  ApolloError,
} from "@apollo/client";
import { organizationsVar } from "@/reactiveVars/user";
import { CURRENT_USER_ORG } from "@/api/queries/currentUserOrg";
import { Organization } from "@/types/Organization";

export const PAGE_SIZE = 100;

export const useUserOrganizations = (): {
  data: Organization[] | null;
  loading: boolean;
  error: ApolloError | undefined;
} => {
  const [getOrganizations, { error: groupOrgError }] = useLazyQuery(
    CURRENT_USER_GROUP_ORGS,
  );
  const { data: defaultOrgData, error: defaultOrgError } =
    useQuery(CURRENT_USER_ORG);

  const organizationsValue = useReactiveVar(organizationsVar.getReactiveVar());

  useEffect(() => {
    if (organizationsValue !== null) return;
    if (!defaultOrgData) return;

    const fetchAllItems = async () => {
      let hasNextPage = true;
      let cursor = null;
      const pages = [];

      while (hasNextPage) {
        const { data } = await getOrganizations({
          variables: {
            after: cursor,
            first: PAGE_SIZE,
          },
        });

        const orgsConnection = data?.me?.officialOrganizations;

        const organizations =
          orgsConnection?.edges?.map((edge) => edge?.node).filter((m) => !!m) ||
          [];

        pages.push(organizations);
        hasNextPage = !!orgsConnection?.pageInfo.hasNextPage;
        cursor = orgsConnection?.pageInfo.endCursor;
      }

      const groupOrgs = pages.flat();

      const defaultOrg = defaultOrgData?.me.officialOrganization;

      // move default org to front of list (if it exists)
      const orgList = groupOrgs.filter((org) => org.id !== defaultOrg?.id);
      if (defaultOrg) orgList.unshift(defaultOrg);

      organizationsVar.set(orgList);
    };
    fetchAllItems();
  }, [getOrganizations, organizationsValue, defaultOrgData]);

  return {
    data: organizationsValue,
    loading: organizationsValue === null,
    error: groupOrgError || defaultOrgError,
  };
};
